import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  CheckCircle, ArrowRight, Sparkles, TrendingUp, Receipt,
  MessageCircle, FileText, Building, Car, DollarSign, Upload,
  Zap, Target, Crown,
} from 'lucide-react';

const HowItWorks = () => {
  const navigate = useNavigate();

  const steps = [
    {
      number: 1,
      icon: Building,
      title: "Connect Your Bank Account",
      description: "Link your business bank account securely with Stripe Financial Connections. We automatically sync your transactions in real-time.",
      image: "/screenshots/bank.png",
      features: [
        "One-click secure bank connection",
        "Automatic transaction sync",
        "Read-only access (we can't move money)",
        "Works with 5,000+ banks",
      ],
    },
    {
      number: 2,
      icon: Upload,
      title: "Import Transactions Instantly",
      description: "Upload CSV files from Google Sheets, QuickBooks, or any bank export. Our AI automatically maps columns and categorizes every transaction.",
      image: "/screenshots/csv_import.png",
      features: [
        "CSV import with smart column mapping",
        "AI categorizes 95% of transactions automatically",
        "Supports all major bank formats",
        "Import years of history in minutes",
      ],
    },
    {
      number: 3,
      icon: Sparkles,
      title: "AI Categorizes Everything",
      description: "Our AI reviews every transaction and assigns the correct IRS category. Switch income/expense types with one click if needed.",
      image: "/screenshots/transactions.png",
      features: [
        "80+ keyword mappings for accuracy",
        "4-level intelligent fallback system",
        "One-click category changes",
        "Toggle between income and expense types",
      ],
    },
    {
      number: 4,
      icon: Receipt,
      title: "Upload Receipts & Documents",
      description: "Snap photos of receipts or upload PDFs. Our OCR technology extracts vendor, amount, and date automatically.",
      image: "/screenshots/receipts.png",
      features: [
        "Smart OCR extracts data from images",
        "Bulk upload up to 20 files at once",
        "Organize by category automatically",
        "Link receipts to transactions",
      ],
    },
    {
      number: 5,
      icon: Car,
      title: "Track Mileage & Deductions",
      description: "Log business trips and we calculate deductions at the IRS rate ($0.67/mile). Track home office, equipment, and all business expenses.",
      image: "/screenshots/mileage.png",
      features: [
        "Simple mileage logging",
        "Automatic deduction calculation",
        "Round-trip tracking",
        "Year-over-year summaries",
      ],
    },
    {
      number: 6,
      icon: DollarSign,
      title: "See Your Tax Bill — Always",
      description: "Real-time tax estimates update as you add income and expenses. Know exactly what you owe for federal, state, and self-employment tax.",
      image: "/screenshots/dashboard.png",
      features: [
        "Real-time federal + state + SE tax calculations",
        "Supports all 50 states",
        "Quarterly payment tracking",
        "Filing status and deduction optimization",
      ],
    },
    {
      number: 7,
      icon: MessageCircle,
      title: "Chat with AI Tax Advisors",
      description: "Get instant answers from 4 specialized AI tax experts. Ask about deductions, S-Corp strategy, bookkeeping, or business structure.",
      image: "/screenshots/advisors.png",
      features: [
        "4 specialized advisors (CPA, Financial Analyst, Bookkeeper, Attorney)",
        "IRS RAG-powered — cites actual publications",
        "24/7 availability",
        "Multi-turn conversations",
      ],
    },
    {
      number: 8,
      icon: FileText,
      title: "Generate CPA-Ready Reports",
      description: "Export professional tax packages with Schedule C, transaction ledgers, and all documentation. Send directly to your accountant.",
      image: "/screenshots/reports.png",
      features: [
        "8-section comprehensive tax package",
        "Includes Schedule C, W-2s, financial statements",
        "Professional branded PDF",
        "Email directly to your CPA",
      ],
    },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-emerald-50">

      {/* ── Hero ── */}
      <div className="bg-gradient-to-br from-slate-900 via-emerald-900 to-slate-900 py-12 sm:py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <Badge className="bg-emerald-500/20 text-emerald-300 border-emerald-500/50 mb-5 px-4 py-2">
              ✨ See How It Works
            </Badge>

            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-5 leading-tight">
              From Bank Connection to{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-400 to-emerald-600">
                Tax-Ready in 8 Steps
              </span>
            </h1>

            <p className="text-base sm:text-lg text-slate-300 mb-8 max-w-2xl mx-auto">
              See exactly how SolelyTax AI simplifies your entire tax workflow — from transaction import to filing.
            </p>

            {/* Step progress — wraps gracefully on small screens */}
            <div className="flex flex-wrap justify-center items-center gap-1.5 sm:gap-2 mb-2">
              {[1, 2, 3, 4, 5, 6, 7, 8].map(num => (
                <div key={num} className="flex items-center gap-1.5 sm:gap-2">
                  <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-emerald-500 text-white flex items-center justify-center font-bold text-xs sm:text-sm">
                    {num}
                  </div>
                  {num < 8 && <div className="w-4 sm:w-8 h-0.5 bg-emerald-500/50 hidden xs:block" />}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* ── Steps ── */}
      <div className="bg-white">
        <div className="container mx-auto px-4 py-10 sm:py-16">
          <div className="max-w-6xl mx-auto space-y-14 sm:space-y-20">
            {steps.map((step, index) => (
              <div
                key={step.number}
                className={`flex flex-col ${index % 2 === 0 ? 'lg:flex-row' : 'lg:flex-row-reverse'} gap-8 lg:gap-12 items-center`}
              >
                {/* Content */}
                <div className="flex-1 space-y-4 sm:space-y-6 w-full">
                  <div className="flex items-center gap-3 sm:gap-4">
                    <div className="w-12 h-12 sm:w-16 sm:h-16 rounded-2xl bg-gradient-to-br from-emerald-500 to-emerald-600 flex items-center justify-center shadow-lg flex-shrink-0">
                      <step.icon className="w-6 h-6 sm:w-8 sm:h-8 text-white" />
                    </div>
                    <div>
                      <Badge className="bg-emerald-100 text-emerald-800 mb-1.5 text-xs">Step {step.number}</Badge>
                      <h2 className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-900 leading-tight">
                        {step.title}
                      </h2>
                    </div>
                  </div>

                  <p className="text-base sm:text-lg text-gray-700 leading-relaxed">
                    {step.description}
                  </p>

                  <div className="space-y-2.5">
                    {step.features.map((feature, idx) => (
                      <div key={idx} className="flex items-start gap-3">
                        <CheckCircle className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                        <span className="text-sm sm:text-base text-gray-700">{feature}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Screenshot */}
                <div className="flex-1 w-full">
                  <Card className="overflow-hidden shadow-lg sm:shadow-2xl hover:shadow-xl transition-shadow duration-300 border border-emerald-100">
                    <CardContent className="p-0">
                      <img
                        src={step.image}
                        alt={step.title}
                        className="w-full h-auto max-h-64 sm:max-h-96 lg:max-h-none object-cover object-top"
                        loading="lazy"
                      />
                    </CardContent>
                  </Card>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── Benefits grid ── */}
      <div className="bg-slate-50 py-12 sm:py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center mb-10">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-3">
              Everything You Need, Nothing You Don't
            </h2>
            <p className="text-base sm:text-lg text-gray-600">
              Built specifically for 1099 contractors, freelancers, and sole proprietors
            </p>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 max-w-6xl mx-auto">
            {[
              { icon: Zap,         title: "95% Accuracy",    desc: "AI categorization" },
              { icon: Target,      title: "Real-Time",       desc: "Tax estimates" },
              { icon: CheckCircle, title: "CPA-Ready",       desc: "Professional reports" },
              { icon: Crown,       title: "Multi-Language",  desc: "English + Spanish" },
            ].map((benefit, idx) => (
              <Card key={idx} className="text-center hover:shadow-lg transition-shadow">
                <CardContent className="pt-5 pb-5 px-3">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 mx-auto mb-3 rounded-full bg-emerald-100 flex items-center justify-center">
                    <benefit.icon className="w-5 h-5 sm:w-6 sm:h-6 text-emerald-600" />
                  </div>
                  <h3 className="font-bold text-sm sm:text-lg mb-0.5">{benefit.title}</h3>
                  <p className="text-xs sm:text-sm text-gray-600">{benefit.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </div>

      {/* ── CTA ── */}
      <div className="bg-gradient-to-br from-slate-900 via-emerald-900 to-slate-900 py-16 sm:py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <Crown className="w-12 h-12 sm:w-16 sm:h-16 text-amber-400 mx-auto mb-5" />
            <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4">
              Ready to Stop Overpaying?
            </h2>
            <p className="text-base sm:text-xl text-slate-300 mb-8">
              Join the first 100 and lock in{' '}
              <span className="text-amber-400 font-bold">$149/year forever</span>
            </p>

            <div className="bg-amber-500/20 border-2 border-amber-500 rounded-lg p-4 sm:p-6 mb-8 max-w-md mx-auto">
              <p className="text-amber-300 font-bold text-base sm:text-lg">
                ⚡ LIMITED: 100 Founding Spots Available
              </p>
              <p className="text-amber-200 text-xs sm:text-sm mt-1.5">
                Regular price $228/year — save $79/year as a founding member
              </p>
            </div>

            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center">
              <Button
                onClick={() => navigate('/pricing')}
                className="bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white px-6 sm:px-8 py-5 sm:py-6 text-base sm:text-lg font-bold shadow-xl"
              >
                <Crown className="w-5 h-5 mr-2" />
                Claim Founding Spot
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>

              <Button
                onClick={() => navigate('/')}
                variant="outline"
                className="bg-white/10 text-white border-white/30 hover:bg-white/20 px-6 sm:px-8 py-5 sm:py-6 text-base sm:text-lg"
              >
                Start Free Trial
              </Button>
            </div>

            <p className="text-slate-400 text-xs sm:text-sm mt-5">
              No credit card required · 30-day free trial · Cancel anytime
            </p>
          </div>
        </div>
      </div>

      {/* ── Footer ── */}
      <div className="border-t border-gray-200 py-8 bg-white">
        <div className="container mx-auto px-4">
          <div className="flex flex-wrap items-center justify-center gap-4 text-sm text-gray-600">
            <button onClick={() => navigate('/terms')} className="hover:text-emerald-600 transition-colors">Terms</button>
            <span>·</span>
            <button onClick={() => navigate('/privacy')} className="hover:text-emerald-600 transition-colors">Privacy</button>
            <span>·</span>
            <a href="mailto:support@solelytax.ai" className="hover:text-emerald-600 transition-colors">Support</a>
          </div>
          <p className="text-center text-gray-500 text-xs mt-3">© 2026 SolelyTax AI. All rights reserved.</p>
        </div>
      </div>
    </div>
  );
};

export default HowItWorks;
